/**
 * FONTE ÚNICA de texto comercial sobre limites de planos.
 *
 * Nenhum número de plano (mensal, diário, créditos) pode ser escrito à mão na
 * UI: todos os textos são compostos a partir dos `UsageLimits` confirmados
 * pelo servidor (`getPublicCommercialConfig` → `app_settings.usage_limits`),
 * exactamente os mesmos valores que o servidor aplica em `quota.server.ts` e
 * no pipeline Pro.
 */
import type { UsageLimits } from "./commercial-config";
import type { PlanTier } from "./plan-tier";

export type PlanCopyLang = "pt" | "en" | "es" | "fr" | "it" | "de";

export function planCopyLang(lang: string | undefined): PlanCopyLang {
  const value = (lang ?? "pt").slice(0, 2).toLowerCase();
  return (["pt", "en", "es", "fr", "it", "de"] as const).includes(value as PlanCopyLang)
    ? (value as PlanCopyLang)
    : "en";
}

type Dict = {
  routesPerMonth: (monthly: number) => string;
  routesPerMonthWithDaily: (monthly: number, daily: number) => string;
  routesPerDay: (daily: number) => string;
  anonLimitTitle: string;
  anonLimitDesc: (limits: UsageLimits) => string;
  freeLimitTitle: string;
  freeLimitDesc: (limits: UsageLimits) => string;
  premiumDailyTitle: string;
  premiumDailyDesc: (limits: UsageLimits) => string;
  premiumMonthlyTitle: string;
  premiumMonthlyDesc: (limits: UsageLimits) => string;
  proMonthlyReached: (limits: UsageLimits) => string;
  proDailyReached: (limits: UsageLimits) => string;
  upsellPremium: (limits: UsageLimits) => string;
};

const DICTS: Record<PlanCopyLang, Dict> = {
  pt: {
    routesPerMonth: (m) => `Até ${m} roteiros por mês`,
    routesPerMonthWithDaily: (m, d) => `Até ${m} roteiros por mês (máx. ${d}/dia)`,
    routesPerDay: (d) => `Até ${d} roteiros por dia`,
    anonLimitTitle: "Limite mensal grátis atingido",
    anonLimitDesc: (l) =>
      `Como visitante podes gerar ${l.anon.monthly} rotas por mês. Cria uma conta grátis para teres ${l.free.monthly} rotas por mês, ou subscreve Premium para ${l.premium.monthly}/mês.`,
    freeLimitTitle: "Limite mensal atingido",
    freeLimitDesc: (l) =>
      `Atingiste o limite grátis de ${l.free.monthly} rotas/mês. Compra um pack de rotas (sem expirar) ou subscreve Premium para ${l.premium.monthly}/mês.`,
    premiumDailyTitle: "Limite diário atingido",
    premiumDailyDesc: (l) =>
      `Atingiste o limite diário do plano Premium (${l.premium.daily} rotas/dia). Volta amanhã ou compra um pack de rotas para continuar já.`,
    premiumMonthlyTitle: "Limite mensal atingido",
    premiumMonthlyDesc: (l) =>
      `Atingiste o limite mensal do Premium (${l.premium.monthly} rotas/mês). Para continuar este mês, compra um pack de rotas — não expiram e somam-se ao Premium.`,
    proMonthlyReached: (l) => `Limite mensal Pro atingido (${l.pro.monthly} roteiros/mês).`,
    proDailyReached: (l) => `Limite diário Pro atingido (${l.pro.daily} roteiros/dia). Volta amanhã.`,
    upsellPremium: (l) =>
      `Ainda não tens uma subscrição ativa. Desbloqueia ${l.premium.monthly} rotas IA/mês, guia áudio e mais.`,
  },
  en: {
    routesPerMonth: (m) => `Up to ${m} itineraries per month`,
    routesPerMonthWithDaily: (m, d) => `Up to ${m} itineraries per month (max ${d}/day)`,
    routesPerDay: (d) => `Up to ${d} itineraries per day`,
    anonLimitTitle: "Monthly free limit reached",
    anonLimitDesc: (l) =>
      `As a guest you can generate ${l.anon.monthly} routes per month. Sign up free to get ${l.free.monthly}/month, or go Premium for ${l.premium.monthly}/month.`,
    freeLimitTitle: "Monthly limit reached",
    freeLimitDesc: (l) =>
      `You hit your free ${l.free.monthly} routes/month limit. Buy a credit pack (no expiry) or go Premium for ${l.premium.monthly}/month.`,
    premiumDailyTitle: "Daily limit reached",
    premiumDailyDesc: (l) =>
      `You hit your Premium daily limit (${l.premium.daily} routes/day). Come back tomorrow or buy a route pack to continue now.`,
    premiumMonthlyTitle: "Monthly limit reached",
    premiumMonthlyDesc: (l) =>
      `You hit your Premium monthly limit (${l.premium.monthly} routes/month). To keep going this month, buy a route pack — they never expire and stack with Premium.`,
    proMonthlyReached: (l) => `Pro monthly limit reached (${l.pro.monthly} itineraries/month).`,
    proDailyReached: (l) => `Pro daily limit reached (${l.pro.daily} itineraries/day). Come back tomorrow.`,
    upsellPremium: (l) =>
      `You don't have an active subscription yet. Unlock ${l.premium.monthly} AI routes/month, audio guide and more.`,
  },
  es: {
    routesPerMonth: (m) => `Hasta ${m} itinerarios al mes`,
    routesPerMonthWithDaily: (m, d) => `Hasta ${m} itinerarios al mes (máx. ${d}/día)`,
    routesPerDay: (d) => `Hasta ${d} itinerarios al día`,
    anonLimitTitle: "Límite mensual gratuito alcanzado",
    anonLimitDesc: (l) =>
      `Como visitante puedes generar ${l.anon.monthly} rutas al mes. Crea una cuenta gratis para tener ${l.free.monthly}/mes, o suscríbete a Premium para ${l.premium.monthly}/mes.`,
    freeLimitTitle: "Límite mensual alcanzado",
    freeLimitDesc: (l) =>
      `Alcanzaste el límite gratuito de ${l.free.monthly} rutas/mes. Compra un pack de rutas o suscríbete a Premium para ${l.premium.monthly}/mes.`,
    premiumDailyTitle: "Límite diario alcanzado",
    premiumDailyDesc: (l) =>
      `Alcanzaste el límite diario de Premium (${l.premium.daily} rutas/día). Vuelve mañana o compra un pack de rutas.`,
    premiumMonthlyTitle: "Límite mensual alcanzado",
    premiumMonthlyDesc: (l) =>
      `Alcanzaste el límite mensual de Premium (${l.premium.monthly} rutas/mes). Compra un pack de rutas — no caducan y se suman a Premium.`,
    proMonthlyReached: (l) => `Límite mensual Pro alcanzado (${l.pro.monthly} itinerarios/mes).`,
    proDailyReached: (l) => `Límite diario Pro alcanzado (${l.pro.daily} itinerarios/día). Vuelve mañana.`,
    upsellPremium: (l) =>
      `Aún no tienes una suscripción activa. Desbloquea ${l.premium.monthly} rutas IA/mes, guía de audio y más.`,
  },
  fr: {
    routesPerMonth: (m) => `Jusqu'à ${m} itinéraires par mois`,
    routesPerMonthWithDaily: (m, d) => `Jusqu'à ${m} itinéraires par mois (max ${d}/jour)`,
    routesPerDay: (d) => `Jusqu'à ${d} itinéraires par jour`,
    anonLimitTitle: "Limite mensuelle gratuite atteinte",
    anonLimitDesc: (l) =>
      `En tant que visiteur tu peux générer ${l.anon.monthly} itinéraires par mois. Crée un compte gratuit pour ${l.free.monthly}/mois, ou passe en Premium pour ${l.premium.monthly}/mois.`,
    freeLimitTitle: "Limite mensuelle atteinte",
    freeLimitDesc: (l) =>
      `Tu as atteint la limite gratuite de ${l.free.monthly} itinéraires/mois. Achète un pack ou passe en Premium pour ${l.premium.monthly}/mois.`,
    premiumDailyTitle: "Limite quotidienne atteinte",
    premiumDailyDesc: (l) =>
      `Tu as atteint la limite quotidienne Premium (${l.premium.daily} itinéraires/jour). Reviens demain ou achète un pack.`,
    premiumMonthlyTitle: "Limite mensuelle atteinte",
    premiumMonthlyDesc: (l) =>
      `Tu as atteint la limite mensuelle Premium (${l.premium.monthly} itinéraires/mois). Achète un pack — il n'expire pas.`,
    proMonthlyReached: (l) => `Limite mensuelle Pro atteinte (${l.pro.monthly} itinéraires/mois).`,
    proDailyReached: (l) => `Limite quotidienne Pro atteinte (${l.pro.daily} itinéraires/jour). Reviens demain.`,
    upsellPremium: (l) =>
      `Tu n'as pas encore d'abonnement actif. Débloque ${l.premium.monthly} itinéraires IA/mois, le guide audio et plus.`,
  },
  it: {
    routesPerMonth: (m) => `Fino a ${m} itinerari al mese`,
    routesPerMonthWithDaily: (m, d) => `Fino a ${m} itinerari al mese (max ${d}/giorno)`,
    routesPerDay: (d) => `Fino a ${d} itinerari al giorno`,
    anonLimitTitle: "Limite mensile gratuito raggiunto",
    anonLimitDesc: (l) =>
      `Come visitatore puoi generare ${l.anon.monthly} itinerari al mese. Crea un account gratuito per ${l.free.monthly}/mese, o passa a Premium per ${l.premium.monthly}/mese.`,
    freeLimitTitle: "Limite mensile raggiunto",
    freeLimitDesc: (l) =>
      `Hai raggiunto il limite gratuito di ${l.free.monthly} itinerari/mese. Acquista un pacchetto o passa a Premium per ${l.premium.monthly}/mese.`,
    premiumDailyTitle: "Limite giornaliero raggiunto",
    premiumDailyDesc: (l) =>
      `Hai raggiunto il limite giornaliero Premium (${l.premium.daily} itinerari/giorno). Torna domani o acquista un pacchetto.`,
    premiumMonthlyTitle: "Limite mensile raggiunto",
    premiumMonthlyDesc: (l) =>
      `Hai raggiunto il limite mensile Premium (${l.premium.monthly} itinerari/mese). Acquista un pacchetto — non scade.`,
    proMonthlyReached: (l) => `Limite mensile Pro raggiunto (${l.pro.monthly} itinerari/mese).`,
    proDailyReached: (l) => `Limite giornaliero Pro raggiunto (${l.pro.daily} itinerari/giorno). Torna domani.`,
    upsellPremium: (l) =>
      `Non hai ancora un abbonamento attivo. Sblocca ${l.premium.monthly} itinerari IA/mese, audioguida e altro.`,
  },
  de: {
    routesPerMonth: (m) => `Bis zu ${m} Routen pro Monat`,
    routesPerMonthWithDaily: (m, d) => `Bis zu ${m} Routen pro Monat (max. ${d}/Tag)`,
    routesPerDay: (d) => `Bis zu ${d} Routen pro Tag`,
    anonLimitTitle: "Kostenloses Monatslimit erreicht",
    anonLimitDesc: (l) =>
      `Als Gast kannst du ${l.anon.monthly} Routen pro Monat erstellen. Registriere dich kostenlos für ${l.free.monthly}/Monat oder hole Premium für ${l.premium.monthly}/Monat.`,
    freeLimitTitle: "Monatslimit erreicht",
    freeLimitDesc: (l) =>
      `Du hast das kostenlose Limit von ${l.free.monthly} Routen/Monat erreicht. Kaufe ein Paket oder hole Premium für ${l.premium.monthly}/Monat.`,
    premiumDailyTitle: "Tageslimit erreicht",
    premiumDailyDesc: (l) =>
      `Du hast das Premium-Tageslimit erreicht (${l.premium.daily} Routen/Tag). Komm morgen wieder oder kaufe ein Paket.`,
    premiumMonthlyTitle: "Monatslimit erreicht",
    premiumMonthlyDesc: (l) =>
      `Du hast das Premium-Monatslimit erreicht (${l.premium.monthly} Routen/Monat). Kaufe ein Paket — es verfällt nicht.`,
    proMonthlyReached: (l) => `Pro-Monatslimit erreicht (${l.pro.monthly} Routen/Monat).`,
    proDailyReached: (l) => `Pro-Tageslimit erreicht (${l.pro.daily} Routen/Tag). Komm morgen wieder.`,
    upsellPremium: (l) =>
      `Du hast noch kein aktives Abo. Schalte ${l.premium.monthly} KI-Routen/Monat, Audioguide und mehr frei.`,
  },
};

export function planCopy(lang?: string): Dict {
  return DICTS[planCopyLang(lang)];
}

/** Linha canónica de limites para um plano, composta a partir da config. */
export function planLimitLine(tier: PlanTier | "anon", limits: UsageLimits, lang?: string): string {
  const dict = planCopy(lang);
  if (tier === "anon") return dict.routesPerMonth(limits.anon.monthly);
  if (tier === "free") return dict.routesPerMonth(limits.free.monthly);
  const value = tier === "pro" ? limits.pro : limits.premium;
  return dict.routesPerMonthWithDaily(value.monthly, value.daily);
}

export type QuotaCopyKind = "anon_limit" | "free_limit" | "premium_limit" | "premium_monthly_limit";

export function quotaLimitCopy(kind: QuotaCopyKind, limits: UsageLimits, lang?: string): {
  title: string;
  desc: string;
} {
  const dict = planCopy(lang);
  switch (kind) {
    case "anon_limit":
      return { title: dict.anonLimitTitle, desc: dict.anonLimitDesc(limits) };
    case "free_limit":
      return { title: dict.freeLimitTitle, desc: dict.freeLimitDesc(limits) };
    case "premium_limit":
      return { title: dict.premiumDailyTitle, desc: dict.premiumDailyDesc(limits) };
    case "premium_monthly_limit":
    default:
      return { title: dict.premiumMonthlyTitle, desc: dict.premiumMonthlyDesc(limits) };
  }
}
